/**
 * Thin fetch wrapper. Reads the auth token straight from zustand's
 * persisted storage so it works in non-React contexts (route loaders etc.)
 * without needing to thread the store through. Centralizes:
 *   - Authorization header injection
 *   - JSON encoding/decoding
 *   - 401 → forced logout + redirect to /login
 *   - structured error throw so callers can switch on .status
 */
import { useAuthStore } from '../auth/store';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/v1';

export interface ApiError extends Error {
  status: number;
  code?: string;
  data?: unknown;
}

function buildError(message: string, status: number, data?: unknown, code?: string): ApiError {
  const e = new Error(message) as ApiError;
  e.status = status;
  e.data = data;
  e.code = code;
  return e;
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;
  const token = useAuthStore.getState().token;

  const url = new URL(`${BASE_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // Try to parse a JSON body whether the request succeeded or failed; many
  // server-side errors come back with a message/code we want to surface.
  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      // Stale or invalid token — drop and bounce to login. Skip during the
      // login request itself (the form handles the 401 inline).
      if (!path.includes('/auth/login')) {
        useAuthStore.getState().clear();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    const message =
      (typeof parsed === 'object' && parsed && 'message' in parsed
        ? String((parsed as any).message)
        : null) ?? `Request failed: ${res.status}`;
    const code =
      typeof parsed === 'object' && parsed && 'code' in parsed
        ? String((parsed as any).code)
        : undefined;
    throw buildError(message, res.status, parsed, code);
  }

  return parsed as T;
}
